import { Box, Heading, Text, Image, Button, Flex, Icon, VStack, HStack, Circle, useDisclosure, Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, ModalFooter, Alert, AlertIcon } from '@chakra-ui/react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  CheckCircleIcon,
  StarIcon,
  TimeIcon,
  ArrowForwardIcon,
  QuestionOutlineIcon,
  ViewIcon,
  ArrowUpIcon,
} from '@chakra-ui/icons';
import Footer from '../../components/Footer';
import { useContext, useState, useEffect } from 'react';
import { AuthContext } from "../../components/AuthContext";
import { checkUserActionPermission } from '../../utils/userActionUtils';
import { USER_ACTION_TYPES } from '../../services/apiService';

const SdsPage = () => {
  const { isLoggedIn } = useContext(AuthContext);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const navigate = useNavigate();
  const [canPerformAction, setCanPerformAction] = useState(true);
  const [checkingPermission, setCheckingPermission] = useState(false);

  // Check permission when component mounts and user is logged in
  useEffect(() => {
    if (isLoggedIn) {
      checkUserActionPermission(
        USER_ACTION_TYPES.SDS,
        setCheckingPermission,
        setCanPerformAction,
        null // Don't show toast on initial check
      );
    }
  }, [isLoggedIn]);

  const handleTryNowClick = () => {
    if (!isLoggedIn) {
      onOpen();
      return;
    }

    if (!canPerformAction) {
      onOpen(); // Show modal with restriction message
      return;
    }

    navigate("/self-directed-search/brief");
  };

  const handleLoginClick = () => {
    navigate("/login");
  };

  return (
    <Box
      minH="100vh"
      bgGradient="linear(to-r, white, #ebf8ff)"
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
    >
      {/* Main Content */}
      <Flex
        direction={{ base: "column", md: "row" }}
        align="center"
        justify="space-between"
        p={{ base: 6, md: 16 }}
        gap={10}
      >
        {/* Right - Image */}
        <Image
          src="/assets/images/sds.svg"
          alt="Personality Test"
          maxW="400px"
          objectFit="contain"
          alignSelf="flex-end"
        />

        {/* Left - Card with Content */}
        <Box
          flex="1"
          maxW={{ base: "100%", md: "60%" }}
          p={8}
          bg="white"
          borderRadius="2xl"
          boxShadow="0 4px 12px rgba(4, 90, 171, 0.2)"
          border="1px solid"
          borderColor="gray.100"
        >
          <Heading color="brand.500" size="xl" mb={4}>
            Personality Test
          </Heading>

          <Text fontSize="lg" mb={6}>
            Discover careers and majors that fit your interests. This test asks
            you asks you to rate activities and abilities, then generates a
            code with personalized role and program suggestions.
          </Text>

          {/* Icons Row */}
          <HStack spacing={6} mb={6}>
            <VStack spacing={1}>
              <Icon as={CheckCircleIcon} color="green.400" boxSize={6} />
              <Text fontSize="sm">Evidence-based</Text>
            </VStack>
            <VStack spacing={1}>
              <Icon as={StarIcon} color="yellow.400" boxSize={6} />
              <Text fontSize="sm">Personalized results</Text>
            </VStack>
            <VStack spacing={1}>
              <Icon as={TimeIcon} color="blue.400" boxSize={6} />
              <Text fontSize="sm">15–20 minutes</Text>
            </VStack>
          </HStack>

          {checkingPermission ? (
            <Button colorScheme="brand" size="md" isDisabled isLoading>
              Checking Permission...
            </Button>
          ) : !canPerformAction && isLoggedIn ? (
            <VStack spacing={3} align="stretch">
              <Alert status="warning">
                <AlertIcon />
                <Text fontSize="sm">
                  You dont have enough attempts to start the SDS test. Please try again later.
                </Text>
              </Alert>
              <Button onClick={handleTryNowClick} colorScheme="brand" size="md" isDisabled>
                Start Test
              </Button>
            </VStack>
          ) : (
            <Button onClick={handleTryNowClick} colorScheme="brand" size="md">
              Start Test
            </Button>
          )}
        </Box>
      </Flex>

      {/* How It Works Section */}
      <Box py={16} px={{ base: 6, md: 16 }} textAlign="center" bg="white">
        <Heading color="brand.500" size="lg" mb={10}>
          How It Works
        </Heading>

        <HStack spacing={10} justify="center" flexWrap="wrap">
          {/* Step 1 */}
          <VStack spacing={4}>
            <Circle size="60px" bg="blue.100" color="blue.700">
              <Icon as={QuestionOutlineIcon} boxSize={6} />
            </Circle>
            <Text fontWeight="bold">Start the Assessment</Text>
            <Text fontSize="sm" color="gray.600" maxW="150px">
              Begin the Personality Test and answer simple interest questions.
            </Text>
          </VStack>

          {/* Arrow */}
          <Icon
            as={ArrowForwardIcon}
            color="gray.400"
            boxSize={6}
            display={{ base: "none", md: "block" }}
          />

          {/* Step 2 */}
          <VStack spacing={4}>
            <Circle size="60px" bg="blue.100" color="blue.700">
              <Icon as={ViewIcon} boxSize={6} />
            </Circle>
            <Text fontWeight="bold">Rate Activities & Skills</Text>
            <Text fontSize="sm" color="gray.600" maxW="150px">
              Indicate how much you like or can do various tasks.
            </Text>
          </VStack>

          {/* Arrow */}
          <Icon
            as={ArrowForwardIcon}
            color="gray.400"
            boxSize={6}
            display={{ base: "none", md: "block" }}
          />

          {/* Step 3 */}
          <VStack spacing={4}>
            <Circle size="60px" bg="blue.100" color="blue.700">
              <Icon as={TimeIcon} boxSize={6} />
            </Circle>
            <Text fontWeight="bold">Get Your Code</Text>
            <Text fontSize="sm" color="gray.600" maxW="150px">
              See your top three interest types (e.g., R–I–A).
            </Text>
          </VStack>

          {/* Arrow */}
          <Icon
            as={ArrowForwardIcon}
            color="gray.400"
            boxSize={6}
            display={{ base: "none", md: "block" }}
          />

          {/* Step 4 */}
          <VStack spacing={4}>
            <Circle size="60px" bg="blue.100" color="blue.700">
              <Icon as={CheckCircleIcon} boxSize={6} />
            </Circle>
            <Text fontWeight="bold">Explore Matches</Text>
            <Text fontSize="sm" color="gray.600" maxW="150px">
              Browse suggested careers and majors aligned to your code.
            </Text>
          </VStack>
        </HStack>
      </Box>

      {/* Call to Action Section */}
      <Box
        bg="brand.500"
        color="white"
        py={16}
        px={{ base: 6, md: 16 }}
        textAlign="center"
      >
        <Heading color="brand.500" size="lg" mb={4}>
          Ready to Find Your Best‑Fit Careers?
        </Heading>
        <Text fontSize="lg" mb={6}>
          Take the Personality Test and get a personalized RIASEC profile with tailored
          suggestions.
        </Text>
        <Button
          onClick={handleTryNowClick}
          size="lg"
          colorScheme="whiteAlpha"
          bg="white"
          color="brand.500"
          _hover={{ bg: "gray.100" }}
          isDisabled={checkingPermission || (isLoggedIn && !canPerformAction)}
          isLoading={checkingPermission}
        >
          {checkingPermission ? "Checking..." : "Start Test"}
        </Button>
      </Box>

      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {!isLoggedIn ? 'Login Required' : 'Cannot Start SDS Test'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {!isLoggedIn ? (
              <Text>Please log in to take the test.</Text>
            ) : (
              <>
                <Alert status="warning" mb={4}>
                  <AlertIcon />
                  <Text fontWeight="bold">Action Restricted</Text>
                </Alert>
                <Text>
                  You cannot start the SDS test right now. Please try again later.
                </Text>
              </>
            )}
          </ModalBody>
          <ModalFooter>
            {!isLoggedIn ? (
              <>
                <Button colorScheme="blue" onClick={handleLoginClick}>
                  Go to Login
                </Button>
                <Button variant="ghost" onClick={onClose}>
                  Cancel
                </Button>
              </>
            ) : (
              <Button variant="ghost" onClick={onClose}>
                Close
              </Button>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Footer />
    </Box>
  );
};

export default SdsPage;